'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { LoadingDark, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { SearchProductService } from '@musinsa/services-curator';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useSearchProductsInfiniteQuery } from '../../common/hooks';
import { EmptyProductList } from '../../product/components/EmptyProductList';
import { COLUMN_COUNT, chunkArray } from '../../product/utils';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { ProductSearchItem } from './ProductSearchItem';

interface Props {
  className?: string;
  keyword: string;
}

export function VirtualizedSearchedProductList({ className, keyword }: Props) {
  const { products, hasNextPage, isFetchingNextPage, fetchNextPage, isPending, isError, total } =
    useSearchProductsInfiniteQuery({ keyword, size: 20 });
  const { isWebview, service } = useDeviceInfo();

  // 3개씩 그룹화된 행들
  const productRows = useMemo(() => {
    if (!products || products.length === 0) return [];
    return chunkArray(products, COLUMN_COUNT);
  }, [products]);

  const onRangeChanged = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleClickGenerateLink = useCallback(
    (product: SearchProductService, goodsIndex: number) => {
      if (service === SERVICE.MUSINSA) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'curatorlink_copy',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: String(product.goodsNo),
          index: goodsIndex,
          name: 'goods',
          section_name: 'search_goods',
          section_title: product.brandName,
        });
        pushDataLayer('click_button', {
          brand_id: GTM_NOTSET_STRING,
          button_id: 'curatorlink_copy',
          button_name: 'goods',
          category_id: String(product.goodsNo),
          index: `${goodsIndex}`,
          section_index: '1',
          section_name: 'search_goods',
        });
      }
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: {
            page_name: 'curator_product_link',
            section_name: 'SearchResultProducts',
            section_title: '검색 결과 상품',
            position: -1,
            button_id: 'generate_link',
            button_name: '링크 생성하기',
            item_no_list: product.goodsNo,
            keyword,
          },
        });
      }
    },
    [keyword, service],
  );

  const handleClickLikeButton = useCallback(
    (product: SearchProductService) => {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.LIKE_ITEM,
          data: {
            page_name: 'curator_product_link',
            section_name: 'SearchResultProducts',
            section_title: '검색 결과 상품',
            position: -1,
            item_no: Number(product.goodsNo),
            item_name: product.goodsName,
          },
        });
      }
    },
    [service],
  );

  const renderItemContent = useCallback(
    (index: number) => {
      const row = productRows[index];

      // NOTE: react-virtuoso "Zero-sized element" 경고 방지를 위해 빈 placeholder 반환
      if (!row) return <div style={{ minHeight: 1 }} />;

      return (
        <div className="grid grid-cols-3 bg-neutral-color-white">
          {row.map((product, columnIndex) => (
            <div key={product.goodsNo} className="h-full pb-6 bg-white">
              <ProductSearchItem
                item={product as SearchProductService}
                index={index * COLUMN_COUNT + columnIndex}
                className="h-full"
                callbackClickProduct={(item) => {
                  if (service === SERVICE['29CM']) {
                    track({
                      schema: SNOWPLOW_SCHEME.CLICK_ITEM,
                      data: {
                        page_name: 'curator_product_link',
                        section_name: 'SearchResultProducts',
                        section_title: '검색 결과 상품',
                        position: -1,
                        item_no: Number(item.goodsNo),
                        brand_no: Number(item.brandNo),
                        brand_name: item.brandName,
                        large_category_no: item.largeCategoryNo ?? -1,
                        large_category_name: item.largeCategoryName ?? '',
                        middle_category_no: item.middleCategoryNo ?? -1,
                        middle_category_name: item.middleCategoryName ?? '',
                        small_category_no: item.smallCategoryNo ?? -1,
                        small_category_name: item.smallCategoryName ?? '',
                        item_position: index * COLUMN_COUNT + columnIndex + 1,
                        keyword,
                      },
                    });
                  }
                }}
                callbackImpression={(item) => {
                  if (service === SERVICE['29CM']) {
                    track({
                      schema: SNOWPLOW_SCHEME.IMPRESSION_ITEM,
                      data: {
                        page_name: 'curator_product_link',
                        section_name: 'SearchResultProducts',
                        section_title: '검색 결과 상품',
                        item_no: Number(item.goodsNo),
                        brand_no: Number(item.brandNo),
                        brand_name: item.brandName,
                        large_category_no: item.largeCategoryNo ?? -1,
                        large_category_name: item.largeCategoryName ?? '',
                        middle_category_no: item.middleCategoryNo ?? -1,
                        middle_category_name: item.middleCategoryName ?? '',
                        small_category_no: item.smallCategoryNo ?? -1,
                        small_category_name: item.smallCategoryName ?? '',
                        position: -1,
                        item_position: index * COLUMN_COUNT + columnIndex + 1,
                        keyword,
                      },
                    });
                  }
                }}
                callbackClickGenerateLink={() =>
                  handleClickGenerateLink(
                    product as SearchProductService,
                    index * COLUMN_COUNT + columnIndex,
                  )
                }
                onClickLikeButton={(item) => handleClickLikeButton(item)}
              />
            </div>
          ))}
          {/* 빈 셀 채우기 */}
          {Array.from({ length: COLUMN_COUNT - row.length }).map((_, emptyIndex) => (
            <div key={`empty-${emptyIndex}`} />
          ))}
        </div>
      );
    },
    [handleClickGenerateLink, handleClickLikeButton, keyword, productRows, service],
  );

  const computeItemKey = useCallback(
    (index: number) => {
      const row = productRows[index];
      if (!row) return `row-${index}`;
      // row의 모든 goodsNo와 isLiked 상태를 조합하여 고유 키 생성
      return row.map((product) => `${product.goodsNo}-${product.isLiked ? '1' : '0'}`).join('_');
    },
    [productRows],
  );

  // 로딩 상태
  if (isPending) {
    return isWebview ? null : (
      <div className="flex flex-1 items-center justify-center p-4">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  // 에러 상태
  if (isError) {
    return <EmptyProductList />;
  }

  // 빈 상태 - hasNextPage가 false이고 데이터가 없을 때만 NoGoods 표시
  if (products.length === 0 && !hasNextPage && !isFetchingNextPage) {
    return <EmptyProductList />;
  }

  return (
    <div className={clsx('grow flex flex-col', className)}>
      {/* NOTE: 가상화 리스트 위에 엘리먼트가 추가되는경우, calc 값 조정 필요 */}
      <div className="flex-1 h-[calc(100vh-112px)]">
        {products.length > 0 && (
          <div className="safe-area-bottom">
            <div className="flex w-full items-start gap-5 bg-[#F5F5F5] px-4 py-3">
              <Typography colorScheme="gray-600" variant="body13pxReg">
                {total.toLocaleString('ko-KR')}개
              </Typography>
            </div>
            <Virtuoso
              useWindowScroll
              style={{ height: '100%' }}
              totalCount={
                hasNextPage
                  ? productRows.length + COLUMN_COUNT // 더 많은 여유 공간으로 endReached 보장
                  : productRows.length
              }
              itemContent={renderItemContent}
              computeItemKey={computeItemKey}
              overscan={10}
              increaseViewportBy={{ top: 200, bottom: 200 }}
              rangeChanged={onRangeChanged}
            />
          </div>
        )}
      </div>
    </div>
  );
}
